import React, { useState, useEffect } from 'react';
import { getNewsComments, createNewsComment, getBlogComments, createBlogComment, getCommentSettings } from '../apis/commentService';
import { toast } from 'react-toastify';

/**
 * Self-contained comment list + form. Form state lives here so the parent
 * (news/blog detail page) does not re-render on every keystroke.
 * Respects site-wide "allow anonymous comments" setting from comment settings.
 */
export default function ArticleCommentSection({ articleId, articleType, comments, onCommentsChange }) {
  const [body, setBody] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [allowAnonymous, setAllowAnonymous] = useState(true);

  useEffect(() => {
    getCommentSettings()
      .then((res) => {
        if (res.success && res.data) setAllowAnonymous(res.data.allowAnonymousComments !== false);
      })
      .catch(() => {});
  }, []);

  const isNews = articleType === 'news';
  const createComment = isNews ? createNewsComment : createBlogComment;
  const getComments = isNews ? getNewsComments : getBlogComments;
  const nameRequired = !allowAnonymous;
  const canSubmit = body.trim() && (!nameRequired || authorName.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !articleId) return;
    setSubmitting(true);
    try {
      const res = await createComment(articleId, body.trim(), authorName.trim());
      if (res.success) {
        toast.success('Comment posted.');
        setBody('');
        setAuthorName('');
        const updated = await getComments(articleId);
        if (updated.success && updated.data && onCommentsChange) onCommentsChange(updated.data);
      } else {
        toast.error(res.message || 'Failed to submit comment');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 border-t pt-6">
      <h3 className="text-xl font-bold mb-4">Comments</h3>
      <div className="space-y-4 mb-6">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-sm">No comments yet. Be the first to comment.</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-800">{c.authorName || 'Anonymous'}</span>
                <span className="text-xs text-gray-500">
                  {new Date(c.createdAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}
                </span>
              </div>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{c.body}</p>
            </div>
          ))
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          placeholder={nameRequired ? 'Your name (required)' : 'Your name (optional)'}
          value={authorName}
          onChange={(e) => setAuthorName(e.target.value)}
          required={nameRequired}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <textarea
          placeholder="Write a comment..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="submit"
          disabled={submitting || !canSubmit}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : 'Post comment'}
        </button>
      </form>
    </div>
  );
}
